import { useEffect, useRef, useState } from "react";
import { Play, Square, Volume2, HardHat, User, Theater } from "lucide-react";
import { speak } from "@/lib/api";
import { LineTranslation } from "@/components/lesson/LineTranslation";
import { RolePlay } from "@/components/lesson/RolePlay";

export function DialogueStep({ dialogue, onModeChange }) {
  const [rate, setRate] = useState(1);
  const [active, setActive] = useState(-1);
  const [roleplay, setRoleplayState] = useState(false);
  const setRoleplay = (v) => { setRoleplayState(v); onModeChange?.(v); };
  const stopRef = useRef(false);
  const first = dialogue[0]?.speaker;
  const slotOf = (l) => (l.speaker === first ? 0 : 1);

  useEffect(() => {
    window.speechSynthesis?.getVoices();
    return () => {
      window.speechSynthesis?.cancel();
      onModeChange?.(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const playAll = () => {
    if (active >= 0) {
      stopRef.current = true;
      window.speechSynthesis?.cancel();
      setActive(-1);
      return;
    }
    stopRef.current = false;
    const next = (i) => {
      if (stopRef.current || i >= dialogue.length) return setActive(-1);
      setActive(i);
      const u = speak(dialogue[i].text, rate, slotOf(dialogue[i]));
      if (!u) return setActive(-1);
      u.onend = () => next(i + 1);
    };
    next(0);
  };

  if (roleplay) return <RolePlay dialogue={dialogue} onExit={() => setRoleplay(false)} />;

  return (
    <div className="space-y-4" data-testid="lesson-dialogue-container">
      <button
        onClick={() => { window.speechSynthesis?.cancel(); setActive(-1); setRoleplay(true); }}
        data-testid="lesson-roleplay-btn"
        className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2 transition-colors"
      >
        <Theater className="w-5 h-5" /> Jouer un rôle au micro
      </button>
      <div className="flex gap-2">
        <button
          onClick={playAll}
          data-testid="lesson-dialogue-audio-btn"
          className="flex-1 h-12 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-bold flex items-center justify-center gap-2 transition-colors"
        >
          {active >= 0 ? <Square className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          {active >= 0 ? "Stop" : "Écouter tout"}
        </button>
        <button
          onClick={() => setRate(rate === 1 ? 0.75 : 1)}
          data-testid="lesson-dialogue-speed-btn"
          className="h-12 px-4 rounded-xl border-2 border-slate-300 font-bold hover:border-orange-500 transition-colors"
        >
          {rate === 1 ? "1x" : "0.75x"}
        </button>
      </div>
      {dialogue.map((l, i) => {
        const left = l.speaker === first;
        const chef = /chef/i.test(l.role);
        return (
          <div key={`${l.speaker}-${l.text}`} className={`flex gap-2 rise ${left ? "" : "flex-row-reverse"}`} data-testid={`lesson-dialogue-line-${i}`}>
            <div className={`shrink-0 w-10 h-10 rounded-full grid place-items-center ${chef ? "bg-orange-500 text-slate-900" : "bg-slate-700 text-white"}`}>
              {chef ? <HardHat className="w-5 h-5" /> : <User className="w-5 h-5" />}
            </div>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 border-2 transition-colors ${
                active === i ? "border-orange-500 bg-orange-50" : left ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="text-xs font-bold uppercase text-slate-500">{l.speaker} · {l.role}</div>
              <div className="flex items-end gap-2">
                <p className="text-base">{l.text}</p>
                <button
                  onClick={() => speak(l.text, rate, slotOf(l))}
                  aria-label="Écouter la réplique"
                  data-testid={`lesson-dialogue-line-audio-${i}`}
                  className="shrink-0 text-slate-400 hover:text-orange-600 transition-colors"
                >
                  <Volume2 className="w-5 h-5" />
                </button>
              </div>
              <LineTranslation text={l.text} testId={`lesson-dialogue-line-translation-${i}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
